import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Check,
  GitBranch,
  Landmark,
  LoaderCircle,
  Plus,
  Sparkles,
  Star,
  Upload,
  X,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api } from '../lib/api';

interface ReferenceImage {
  id: string;
  originalName: string;
}

interface StudioResult {
  id: string;
  outputAttachmentId: string | null;
  status: 'PENDING' | 'GENERATING' | 'READY' | 'FAILED' | 'CANCELLED';
  modelId: string | null;
  revision: number;
  selected: boolean;
}

interface Project {
  id: string;
  titleAr: string;
  modeKey: string;
  prompt: string;
  results: StudioResult[];
  updatedAt: string;
}

const interventionOptions = [
  { key: 'light', label: 'تدخل خفيف', prompt: 'light intervention' },
  { key: 'medium', label: 'تدخل متوسط', prompt: 'medium intervention' },
  { key: 'strong', label: 'تدخل قوي', prompt: 'strong intervention' },
] as const;

export function LocalArchitectureStudioPage() {
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [intervention, setIntervention] = useState<'light' | 'medium' | 'strong'>('medium');
  const [preserveOpenings, setPreserveOpenings] = useState(true);
  const [preserveMassing, setPreserveMassing] = useState(true);
  const [preserveGeometry, setPreserveGeometry] = useState(true);
  const [materials, setMaterials] = useState('');
  const [colors, setColors] = useState('');
  const [shading, setShading] = useState('');
  const [alternatives, setAlternatives] = useState(3);
  const [references, setReferences] = useState<ReferenceImage[]>([]);
  const [uploading, setUploading] = useState(false);

  const projectsQuery = useQuery({
    queryKey: ['architecture-projects'],
    queryFn: () =>
      api<Project[]>('/api/studios/projects?mode=local_architecture'),
  });
  const projectQuery = useQuery({
    queryKey: ['architecture-project', selectedId],
    queryFn: () => api<Project>(`/api/studios/projects/${selectedId}`),
    enabled: Boolean(selectedId),
    refetchInterval: (query) =>
      query.state.data?.results.some((result) =>
        ['PENDING', 'GENERATING'].includes(result.status),
      )
        ? 3_000
        : false,
  });

  useEffect(() => {
    if (!selectedId && projectsQuery.data?.[0]) {
      setSelectedId(projectsQuery.data[0].id);
    }
  }, [selectedId, projectsQuery.data]);

  function buildPrompt(alternativeIndex: number) {
    const degree =
      interventionOptions.find((option) => option.key === intervention)?.prompt ||
      'medium intervention';
    return [
      'Transform the supplied local architecture facade using the approved Details local architecture constitution.',
      `Intervention degree: ${degree}.`,
      `Preservation constraints: openings=${preserveOpenings ? 'preserve' : 'may adjust'}; massing=${preserveMassing ? 'preserve' : 'may adjust'}; geometry=${preserveGeometry ? 'preserve' : 'may adjust'}.`,
      materials ? `Materials: ${materials}.` : '',
      colors ? `Colors: ${colors}.` : '',
      shading ? `Shading elements: ${shading}.` : '',
      notes ? `User notes: ${notes}.` : '',
      `Generate alternative ${alternativeIndex + 1} of ${alternatives}, genuinely distinct from the other alternatives.`,
      'Keep the result architecturally plausible, technically readable, and suitable for a professional presentation.',
    ]
      .filter(Boolean)
      .join('\n');
  }

  const createProject = useMutation({
    mutationFn: () =>
      api<Project>('/api/studios/projects', {
        method: 'POST',
        body: JSON.stringify({
          titleAr: newTitle,
          modeKey: 'local_architecture',
          prompt: notes || 'Local architecture facade transformation.',
          settings: { intervention },
        }),
      }),
    onSuccess: async (project) => {
      setSelectedId(project.id);
      setShowCreate(false);
      toast.success('أُنشئ مشروع العمارة المحلية');
      await queryClient.invalidateQueries({ queryKey: ['architecture-projects'] });
    },
    onError: (error) => toast.error(error.message),
  });

  const generateAlternatives = useMutation({
    mutationFn: async () => {
      if (!selectedId) throw new Error('اختر مشروعًا أولًا');
      if (references.length === 0) {
        throw new Error('أضف صورة واجهة مرجعية واحدة على الأقل');
      }
      for (let index = 0; index < alternatives; index += 1) {
        await api(`/api/studios/projects/${selectedId}/generate`, {
          method: 'POST',
          body: JSON.stringify({
            prompt: buildPrompt(index),
            referenceAttachmentIds: references.map((reference) => reference.id),
            size: '1536x1024',
            quality: 'high',
          }),
        });
      }
    },
    onSuccess: async () => {
      toast.success(`أُضيفت ${alternatives} بدائل إلى طابور التوليد`);
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['architecture-project', selectedId],
        }),
        queryClient.invalidateQueries({ queryKey: ['architecture-projects'] }),
        queryClient.invalidateQueries({ queryKey: ['jobs'] }),
      ]);
    },
    onError: (error) => toast.error(error.message),
  });

  const selectResult = useMutation({
    mutationFn: (id: string) =>
      api(`/api/studios/results/${id}/select`, { method: 'POST' }),
    onSuccess: async () => {
      toast.success('اعتُمد البديل');
      await queryClient.invalidateQueries({
        queryKey: ['architecture-project', selectedId],
      });
    },
    onError: (error) => toast.error(error.message),
  });

  const branchResult = useMutation({
    mutationFn: (id: string) =>
      api<Project>(`/api/studios/results/${id}/branch`, { method: 'POST' }),
    onSuccess: async (project) => {
      setSelectedId(project.id);
      toast.success('أُنشئ فرع تطوير جديد');
      await queryClient.invalidateQueries({ queryKey: ['architecture-projects'] });
    },
    onError: (error) => toast.error(error.message),
  });

  async function uploadReferences(files: FileList | null) {
    if (!files?.length) return;
    if (references.length + files.length > 4) {
      toast.error('الحد الأقصى 4 صور مرجعية');
      return;
    }
    setUploading(true);
    try {
      const uploaded: ReferenceImage[] = [];
      for (const file of [...files]) {
        const form = new FormData();
        form.append('file', file);
        uploaded.push(
          await api<ReferenceImage>('/api/files/upload', {
            method: 'POST',
            body: form,
          }),
        );
      }
      setReferences((current) => [...current, ...uploaded]);
      toast.success('رُفعت المراجع المعمارية');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'تعذر رفع الصور');
    } finally {
      setUploading(false);
    }
  }

  const project = projectQuery.data;
  return (
    <div className="grid min-h-screen lg:grid-cols-[290px_1fr]">
      <aside className="border-l border-ink/10 bg-white/60 p-4">
        <div className="mb-5 flex items-center justify-between">
          <div>
            <p className="text-[10px] font-bold text-slate-400">Local Architecture</p>
            <h1 className="mt-1 text-lg font-black">مشاريع الواجهات</h1>
          </div>
          <button type="button" onClick={() => setShowCreate((value) => !value)} className="grid h-9 w-9 place-items-center rounded-xl bg-ink text-white hover:bg-teal" aria-label="مشروع معماري جديد">
            <Plus className="h-4 w-4" />
          </button>
        </div>
        {showCreate && (
          <div className="mb-4 space-y-2 rounded-2xl border border-teal/20 bg-teal/5 p-3">
            <input value={newTitle} onChange={(event) => setNewTitle(event.target.value)} placeholder="اسم المشروع" className="w-full rounded-lg border border-ink/10 bg-white px-2.5 py-2 text-[11px] outline-none focus:border-teal" />
            <textarea value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="وصف الواجهة والهدف" rows={3} className="w-full resize-none rounded-lg border border-ink/10 bg-white px-2.5 py-2 text-[11px] outline-none focus:border-teal" />
            <button type="button" onClick={() => createProject.mutate()} disabled={!newTitle || notes.length < 3 || createProject.isPending} className="w-full rounded-lg bg-teal px-3 py-2 text-[10px] font-black text-white disabled:opacity-40">إنشاء المشروع</button>
          </div>
        )}
        <div className="space-y-1">
          {projectsQuery.data?.map((item) => (
            <button key={item.id} type="button" onClick={() => setSelectedId(item.id)} className={`w-full rounded-xl px-3 py-3 text-start ${selectedId === item.id ? 'bg-ink text-white' : 'hover:bg-white'}`}>
              <p className="truncate text-xs font-black">{item.titleAr}</p>
              <p className={`mt-1 text-[9px] ${selectedId === item.id ? 'text-white/40' : 'text-slate-400'}`}>{item.results.length} بديل</p>
            </button>
          ))}
        </div>
      </aside>

      <section className="p-4 sm:p-7">
        {!project ? (
          <div className="grid min-h-[70vh] place-items-center text-center">
            <div>
              <Landmark className="mx-auto mb-4 h-10 w-10 text-teal" />
              <h2 className="text-xl font-black">استوديو العمارة المحلية</h2>
              <p className="mt-2 text-xs font-semibold text-slate-500">ابدأ مشروعًا لتحويل الواجهة وفق دستور العمارة المحلية.</p>
            </div>
          </div>
        ) : (
          <>
            <header className="mb-5">
              <p className="text-[10px] font-black text-teal">Source → Analyze → Generate → Compare → Approve</p>
              <h2 className="mt-1 text-2xl font-black">{project.titleAr}</h2>
            </header>
            <div className="grid gap-5 xl:grid-cols-[390px_1fr]">
              <div className="space-y-4">
                <section className="rounded-2xl border border-ink/10 bg-white p-4">
                  <h3 className="mb-3 text-sm font-black">المرجع المعماري</h3>
                  <label className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-teal/30 bg-teal/5 px-4 py-5 text-[10px] font-black text-teal">
                    {uploading ? <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                    إضافة واجهة أو مرجع
                    <input type="file" multiple accept=".png,.jpg,.jpeg" className="hidden" onChange={(event) => void uploadReferences(event.target.files)} />
                  </label>
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    {references.map((reference) => (
                      <div key={reference.id} className="relative aspect-square overflow-hidden rounded-xl bg-slate-100">
                        <img src={`/api/files/${reference.id}?inline=1`} alt={reference.originalName} className="h-full w-full object-cover" />
                        <button type="button" onClick={() => setReferences((current) => current.filter((item) => item.id !== reference.id))} className="absolute end-1.5 top-1.5 grid h-6 w-6 place-items-center rounded-lg bg-ink/70 text-white"><X className="h-3 w-3" /></button>
                      </div>
                    ))}
                  </div>
                </section>

                <section className="rounded-2xl border border-ink/10 bg-white p-4">
                  <h3 className="mb-3 text-sm font-black">قيود التوليد</h3>
                  <label className="block">
                    <span className="mb-1.5 block text-[9px] font-black text-slate-500">درجة التدخل</span>
                    <select value={intervention} onChange={(event) => setIntervention(event.target.value as typeof intervention)} className="w-full rounded-xl border border-ink/10 bg-slate-50 px-3 py-2.5 text-xs outline-none focus:border-teal">
                      {interventionOptions.map((option) => <option key={option.key} value={option.key}>{option.label}</option>)}
                    </select>
                  </label>
                  <div className="mt-3 space-y-2">
                    {[
                      ['حفظ الفتحات', preserveOpenings, setPreserveOpenings],
                      ['حفظ الكتل', preserveMassing, setPreserveMassing],
                      ['حفظ الهندسة', preserveGeometry, setPreserveGeometry],
                    ].map(([label, value, setter]) => (
                      <label key={String(label)} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-[10px] font-bold">
                        {String(label)}
                        <input type="checkbox" checked={Boolean(value)} onChange={(event) => (setter as (v: boolean) => void)(event.target.checked)} className="h-4 w-4 accent-teal" />
                      </label>
                    ))}
                  </div>
                  <div className="mt-3 space-y-2">
                    <input value={materials} onChange={(event) => setMaterials(event.target.value)} placeholder="الخامات المطلوبة" className="w-full rounded-xl border border-ink/10 bg-slate-50 px-3 py-2.5 text-xs outline-none focus:border-teal" />
                    <input value={colors} onChange={(event) => setColors(event.target.value)} placeholder="الألوان" className="w-full rounded-xl border border-ink/10 bg-slate-50 px-3 py-2.5 text-xs outline-none focus:border-teal" />
                    <input value={shading} onChange={(event) => setShading(event.target.value)} placeholder="عناصر التظليل" className="w-full rounded-xl border border-ink/10 bg-slate-50 px-3 py-2.5 text-xs outline-none focus:border-teal" />
                    <textarea value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="ملاحظات إضافية" rows={3} className="w-full resize-none rounded-xl border border-ink/10 bg-slate-50 px-3 py-2.5 text-xs outline-none focus:border-teal" />
                  </div>
                  <label className="mt-3 block">
                    <span className="mb-1.5 block text-[9px] font-black text-slate-500">عدد البدائل</span>
                    <select value={alternatives} onChange={(event) => setAlternatives(Number(event.target.value))} className="w-full rounded-xl border border-ink/10 bg-slate-50 px-3 py-2.5 text-xs outline-none focus:border-teal">
                      <option value={2}>بديلان</option>
                      <option value={3}>3 بدائل</option>
                      <option value={4}>4 بدائل</option>
                    </select>
                  </label>
                </section>

                <button type="button" onClick={() => generateAlternatives.mutate()} disabled={references.length === 0 || generateAlternatives.isPending} className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-ink px-4 py-3 text-sm font-black text-white hover:bg-teal disabled:opacity-40">
                  {generateAlternatives.isPending ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                  توليد {alternatives} بدائل
                </button>
              </div>

              <section>
                {project.results.length ? (
                  <div className="grid gap-4 md:grid-cols-2">
                    {project.results.map((result) => (
                      <article key={result.id} className={`overflow-hidden rounded-2xl border bg-white ${result.selected ? 'border-gold ring-2 ring-gold/15' : 'border-ink/10'}`}>
                        <div className="relative aspect-[3/2] bg-slate-100">
                          {result.outputAttachmentId ? (
                            <img src={`/api/files/${result.outputAttachmentId}?inline=1`} alt={`بديل ${result.revision}`} className="h-full w-full object-cover" />
                          ) : (
                            <div className="grid h-full place-items-center">
                              {result.status === 'FAILED' ? <X className="h-7 w-7 text-red-400" /> : <LoaderCircle className="h-7 w-7 animate-spin text-teal" />}
                            </div>
                          )}
                          {result.selected && <span className="absolute end-3 top-3 inline-flex items-center gap-1 rounded-full bg-gold px-2 py-1 text-[8px] font-black text-white"><Check className="h-2.5 w-2.5" /> معتمد</span>}
                        </div>
                        <div className="flex items-center justify-between gap-2 p-3">
                          <span className="text-[10px] font-black">بديل {result.revision}</span>
                          <div className="flex gap-1.5">
                            {result.outputAttachmentId && <button type="button" onClick={() => selectResult.mutate(result.id)} className="rounded-lg bg-gold/10 p-2 text-gold" aria-label="اعتماد البديل"><Star className="h-3.5 w-3.5" /></button>}
                            <button type="button" onClick={() => branchResult.mutate(result.id)} className="rounded-lg bg-teal/10 p-2 text-teal" aria-label="إنشاء فرع"><GitBranch className="h-3.5 w-3.5" /></button>
                          </div>
                        </div>
                      </article>
                    ))}
                  </div>
                ) : (
                  <div className="grid min-h-[55vh] place-items-center rounded-3xl border border-dashed border-ink/15 bg-white/40 text-center">
                    <div>
                      <Landmark className="mx-auto mb-3 h-9 w-9 text-teal" />
                      <p className="text-sm font-black">لم تُولد بدائل بعد</p>
                      <p className="mt-1 text-[10px] font-semibold text-slate-500">أضف مرجع الواجهة واضبط قيود الحفظ ثم ابدأ.</p>
                    </div>
                  </div>
                )}
              </section>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
